import type { CoreTopicDTO, CourseStructureNodeDTO, EOHierarchyDTO } from '../services/adminCourseService';

/** Collect topic ids from a nested core topic tree (used for subject-scoped filtering). */
export function collectCoreTopicIds(topics: CoreTopicDTO[]): Set<number> {
  const ids = new Set<number>();
  const walk = (list: CoreTopicDTO[]) => {
    for (const t of list) {
      ids.add(t.id);
      if (t.children?.length) walk(t.children);
    }
  };
  walk(topics);
  return ids;
}

/** Stable key for matching curriculum scaffold nodes (area / group / EO) to persisted rows. */
export function curriculumStructureNodeKey(node: CourseStructureNodeDTO): string {
  return `${node.nodeType}:${node.refId ?? 0}`;
}

/** Build the virtual Area → Group → EO tree from Scottish curriculum hierarchy. */
export function buildCurriculumStructureFromEoHierarchy(
  eoHierarchy: EOHierarchyDTO[],
  courseId: number
): CourseStructureNodeDTO[] {
  let idCounter = -1000;

  const transform = (
    items: EOHierarchyDTO[] | EOHierarchyDTO['groups'] | NonNullable<EOHierarchyDTO['groups']>[0]['experienceOutcomes'],
    type: 'AREA' | 'GROUP' | 'EO'
  ): CourseStructureNodeDTO[] => {
    if (!items || !Array.isArray(items)) return [];
    return (items as Array<Record<string, unknown>>).filter(Boolean).map((item) => {
      idCounter--;
      let label = '';
      let children: CourseStructureNodeDTO[] = [];
      let refId = 0;

      if (type === 'AREA') {
        const area = item as EOHierarchyDTO;
        label = area.areaName || 'Unknown Area';
        refId = area.areaId;
        if (area.groups) children = transform(area.groups, 'GROUP');
      } else if (type === 'GROUP') {
        const group = item as NonNullable<EOHierarchyDTO['groups']>[0];
        label = group.groupName || 'Unknown Group';
        refId = group.groupId;
        if (group.experienceOutcomes) children = transform(group.experienceOutcomes, 'EO');
      } else {
        const eo = item as NonNullable<NonNullable<EOHierarchyDTO['groups']>[0]['experienceOutcomes']>[0];
        label = `${eo.code || '?'}: ${eo.description || ''}`;
        refId = eo.eoId;
      }

      return {
        id: idCounter,
        courseId,
        label,
        nodeType: type === 'EO' ? ('CURRICULUM_REF' as const) : ('WRAPPER' as const),
        refId: refId || 0,
        viewId: 0,
        parentId: 0,
        displayOrder: 0,
        isVisible: true,
        isExpanded: true,
        children,
        deleteEnabled: false,
        editEnabled: false,
      };
    });
  };

  return transform(eoHierarchy, 'AREA');
}

export function indexPersistedCurriculumNodes(
  nodes: CourseStructureNodeDTO[],
  map = new Map<string, CourseStructureNodeDTO>()
): Map<string, CourseStructureNodeDTO> {
  for (const node of nodes) {
    if (node.nodeType !== 'TOPIC_REF') {
      map.set(curriculumStructureNodeKey(node), node);
    }
    if (node.children?.length) {
      indexPersistedCurriculumNodes(node.children, map);
    }
  }
  return map;
}

/** Collect keys for every node in the curriculum template tree. */
export function collectCurriculumTemplateKeys(template: CourseStructureNodeDTO[]): Set<string> {
  const keys = new Set<string>();
  const walk = (nodes: CourseStructureNodeDTO[]) => {
    for (const n of nodes) {
      keys.add(curriculumStructureNodeKey(n));
      if (n.children?.length) walk(n.children);
    }
  };
  walk(template);
  return keys;
}

/** Overlay persisted draft nodes onto the curriculum template so Area/Group/EO scaffolding stays visible. */
export function mergeCurriculumScaffoldWithPersistedNodes(
  template: CourseStructureNodeDTO[],
  persistedRoots: CourseStructureNodeDTO[]
): CourseStructureNodeDTO[] {
  if (persistedRoots.length === 0) return template;

  const dbByKey = indexPersistedCurriculumNodes(persistedRoots);
  const templateKeys = collectCurriculumTemplateKeys(template);

  const mergeNode = (templateNode: CourseStructureNodeDTO): CourseStructureNodeDTO => {
    const dbNode = dbByKey.get(curriculumStructureNodeKey(templateNode));
    const scaffoldChildren = (templateNode.children ?? []).filter((c) => c.nodeType !== 'TOPIC_REF');
    const topicChildren = (dbNode?.children ?? []).filter((c) => c.nodeType === 'TOPIC_REF');

    return {
      ...templateNode,
      id: dbNode?.id ?? templateNode.id,
      isVisible: dbNode?.isVisible ?? templateNode.isVisible,
      isExpanded: dbNode?.isExpanded ?? templateNode.isExpanded,
      children: [...scaffoldChildren.map(mergeNode), ...topicChildren],
    };
  };

  const mergedTemplate = template.map(mergeNode);
  // Custom wrapper/topic branches (not part of Area/Group/EO scaffold) live at persisted roots.
  const customRoots = persistedRoots.filter(
    (n) => !templateKeys.has(curriculumStructureNodeKey(n))
  );

  return [...mergedTemplate, ...customRoots];
}

/** Drop TOPIC_REF nodes whose refId is not in the current subject's core topic library,
 *  except course-local custom topics explicitly scoped to this course. */
export function filterTopicRefsByAllowedIds(
  nodes: CourseStructureNodeDTO[],
  allowedTopicIds: Set<number>,
  courseLocalTopicIds: Set<number> = new Set()
): CourseStructureNodeDTO[] {
  return nodes
    .filter((n) => {
      if (n.nodeType !== 'TOPIC_REF' || n.refId == null) return true;
      // Persisted structure rows from the DB are authoritative for this course.
      if (n.id > 0) return true;
      return allowedTopicIds.has(n.refId) || courseLocalTopicIds.has(n.refId);
    })
    .map((n) => ({
      ...n,
      children: n.children
        ? filterTopicRefsByAllowedIds(n.children, allowedTopicIds, courseLocalTopicIds)
        : n.children,
    }));
}

export function collectTopicRefIds(nodes: CourseStructureNodeDTO[]): number[] {
  const ids: number[] = [];
  const walk = (list: CourseStructureNodeDTO[]) => {
    for (const n of list) {
      if (n.nodeType === 'TOPIC_REF' && n.refId != null) ids.push(n.refId);
      if (n.children?.length) walk(n.children);
    }
  };
  walk(nodes);
  return ids;
}
